import { useFieldApi } from '@data-driven-forms/react-form-renderer';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import RadioGroup from '@mui/material/RadioGroup';
import { Fragment, useEffect, useRef } from 'react';

import CalculationCard from '~/common/components/CalculationCard';
import FormLabel from '~/common/components/FormLabel';
import HStack from '~/common/components/Stacks/HStack';
import MUIRadio from '~/common/mui/Radios/MUIRadioGroup/Radio';
import { StyledAdditionalNotes } from '~/common/mui/Radios/MUIRadioGroup/StyledRadioElements.js';

import { validationError } from '../utils';
import LabelInput from './LabelInput';

const LABEL_INPUT_PROPS_DEFAULT = {
  name: '',
  type: 'text',
  snippets: [],
  toggleInputOnSelection: false,
  focusInputOnSelection: false,
  optionsToRenderInput: [],
  optionLabel: '',
  hasExtraction: false,
  componentType: 'Input',
};

type RadioProps = {
  name?: string;
  parentId?: string;
  helperText?: string;
  noteText?: string;
  noteTextSx?: any;
  onChange?: (...args: any[]) => any;
  onFocus?: (...args: any[]) => any;
  renderAs?: any;
  sx?: any;
  styledRadioSx?: any;
  labelInputProps?: any;
  isDisabled?: boolean;
  options: {
    label: string;
    value: string | number;
    isDisabled?: boolean;
  }[];
  label?: string;
};

export default function Radio({
  name,
  sx,
  helperText,
  noteText,
  noteTextSx,
  renderAs,
  onChange,
  onFocus,
  isDisabled,
  labelInputProps = LABEL_INPUT_PROPS_DEFAULT,
  ...props
}: Readonly<RadioProps>) {
  const {
    label,
    options,
    input,
    isRequired,
    meta,
    validateOnMount,
    fcStyle,
    lbStyle,
    flStyle,
    parentId,
    styledRadioSx,
  } = useFieldApi({
    ...props,
    name,
  });

  const labelInputRef = useRef(null);
  const invalid = validationError(meta, validateOnMount);
  const text = invalid || ((meta.touched || validateOnMount) && meta.warning);
  const { focusInputOnSelection, optionsToRenderInput, type } = labelInputProps;

  const handleFocusOnSelection = (e) => {
    if (
      focusInputOnSelection &&
      optionsToRenderInput.includes(e) &&
      labelInputRef.current
    ) {
      type === 'date' //INFO: React DatePicker uses setFocus instead of focus
        ? labelInputRef.current.setFocus(true)
        : labelInputRef.current.focus();
    }
  };

  useEffect(() => {
    if (input) {
      input.onChange(`${input.value}`);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const RenderRadioGroup = (isCalcCard) => (
    <RadioGroup
      name={name}
      data-testid={`${name}-radioGroup`}
      value={meta.initial}
      row
      {...input}
      onChange={(e) => {
        handleFocusOnSelection(e);
        input.onChange(e);
        onChange?.(e);
      }}
      onFocus={(e) => {
        input.onFocus(e);
        onFocus?.(e);
      }}
      sx={{ flexWrap: 'unset', display: 'unset' }}
      id={parentId}
    >
      <HStack sx={{ ...sx }}>
        {options.map((option, i) => {
          const displayLabelInput = optionsToRenderInput?.includes(
            option.label,
          );
          const commonProps = {
            key: i,
            value: option.value,
            label: option.label,
            sx: {
              gap: 'unset',
              marginLeft: '-8px',
              ...(isCalcCard && { alignItems: 'unset' }),
              ...lbStyle,
            },
            'aria-invalid': invalid,
            'aria-errormessage': `${name}-error`,
          };

          return (
            <Fragment key={`card-${option.label}`}>
              <FormControlLabel
                {...commonProps}
                control={
                  isCalcCard ? (
                    <CalculationCard
                      {...option}
                      isChecked={input.value === option.value}
                      onClick={(e) => {
                        input.onChange(option.value);
                        onChange?.(e);
                      }}
                    />
                  ) : (
                    <MUIRadio
                      sx={{ marginLeft: 'unset', ...styledRadioSx }}
                      data-testid={`${name}-${option.value}`}
                      error={invalid}
                      checked={input.value === option.value}
                      disabled={option.isDisabled}
                    />
                  )
                }
              />
              {displayLabelInput && (
                <LabelInput
                  ref={labelInputRef}
                  key={`labelInput-${option.label}`}
                  optionLabel={option.label}
                  selectedOption={input?.value}
                  onChange={input?.onChange}
                  {...labelInputProps}
                />
              )}
            </Fragment>
          );
        })}
      </HStack>
    </RadioGroup>
  );

  return (
    <FormControl
      id={name}
      required={isRequired}
      error={!!invalid}
      sx={{ ...fcStyle }}
      component="fieldset"
      disabled={isDisabled}
    >
      {label && (
        <FormLabel
          label={label}
          tooltip={helperText}
          required={isRequired}
          sx={{ fontWeight: 500, ...flStyle }}
        />
      )}
      {!!noteText && (
        <StyledAdditionalNotes
          className="Mui-additional-notes"
          mb={'0.5rem'}
          sx={noteTextSx}
        >
          {noteText}
        </StyledAdditionalNotes>
      )}

      {options &&
        options.length > 0 &&
        RenderRadioGroup(renderAs === 'CalculationCard')}

      {!!invalid && (
        <FormHelperText id={`${name}-error`} sx={{ fontSize: '0.875rem' }}>
          {text}
        </FormHelperText>
      )}
    </FormControl>
  );
}
